import { cn } from "@/utils";
import {
  Checkbox,
  Description,
  FieldError,
  Header,
  Label,
  ListBox,
  SearchField,
  Select,
  Separator,
  Skeleton,
} from "@heroui-v3/react";
import { t } from "@lingui/core/macro";
import { Check, List, ListChecks, Plus } from "lucide-react";
import {
  Fragment,
  useCallback,
  useMemo,
  useState,
  type Key,
  type ReactNode,
} from "react";
import { SELECT_ALL_ITEM_KEY } from "./multiSelectChange";
import { filterSelectItems } from "./selectSearch";

const CREATE_NEW_KEY = "__CREATE_NEW__";

export type CreateNewOption = {
  customLabel?: ReactNode;
  icon?: ReactNode;
  label: string;
  onCreateNew: () => void;
  sectionName?: string;
};

export type LimeSelectV3Item = {
  customLabel?: ReactNode;
  endContent?: ReactNode;
  key: string | number;
  label: string;
  sectionName?: string;
  startContent?: ReactNode;
};

export type LimeSelectV3Props = {
  /** For a select without a visible label. */
  "aria-label"?: string;
  className?: string;
  /** Restyles the trigger, e.g. to sit in a toolbar beside outline buttons. */
  triggerClassName?: string;
  createNewOption?: CreateNewOption;
  description?: ReactNode;
  disabledKeys?: Iterable<Key>;
  disallowEmptySelection?: boolean;
  emptyContentMessage?: string;
  error?: ReactNode;
  "data-identifier"?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  isRequired?: boolean;
  isSearchable?: boolean;
  isSkeleton?: boolean;
  items: LimeSelectV3Item[];
  label?: ReactNode;
  name?: string;
  onChange?: (value: string) => void;
  onMultipleItemChange?: (value: string[]) => void;
  onOpenChange?: (isOpen: boolean) => void;
  onSingleItemChange?: (value: string) => void;
  placeholder?: string;
  /** Replaces the trigger's text for a selection, e.g. `summarizeSelection`. */
  renderValue?: (selected: LimeSelectV3Item[]) => ReactNode;
  searchPlaceholder?: string;
  selectionMode?: "single" | "multiple";
  showSectionSelectAllToggle?: boolean;
  showSelectAllToggle?: boolean;
  /** Shown in the trigger before the value, e.g. an icon. */
  startContent?: ReactNode;
  value?: unknown;
  variant?: "primary" | "secondary";
};

/**
 * change handlers ( omit default hanlder )
 * - when using single mode : onSingleItemChange
 * - when using multiple mode : onMultipleItemChange
 *
 * for value:
 * - use value prop instead of selectedKeys
 */
export function LimeSelectV3({
  "aria-label": ariaLabel,
  className,
  triggerClassName,
  createNewOption,
  description,
  disabledKeys,
  disallowEmptySelection = true,
  emptyContentMessage = t`Ni rezultatov`,
  error,
  "data-identifier": dataIdentifier,
  isDisabled,
  isInvalid,
  isLoading,
  isOpen,
  isRequired,
  isSearchable = false,
  isSkeleton = false,
  items,
  label,
  name,
  onChange,
  onMultipleItemChange,
  onOpenChange,
  onSingleItemChange,
  placeholder,
  renderValue,
  searchPlaceholder,
  selectionMode = "single",
  showSectionSelectAllToggle = false,
  showSelectAllToggle = false,
  startContent,
  value,
  variant,
}: LimeSelectV3Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const isMultiple = selectionMode === "multiple";
  const disabledKeySet = useMemo(
    () => new Set(disabledKeys == null ? [] : Array.from(disabledKeys, String)),
    [disabledKeys],
  );
  const normalizedItems = useMemo(
    () => items.map((item) => ({ ...item, key: String(item.key) })),
    [items],
  );

  const allItems = useMemo(() => {
    if (!createNewOption) return normalizedItems;

    return [
      {
        customLabel: createNewOption.customLabel,
        endContent: undefined,
        key: CREATE_NEW_KEY,
        label: createNewOption.label,
        sectionName: createNewOption.sectionName,
        startContent: createNewOption.icon ?? <Plus size={16} />,
      },
      ...normalizedItems,
    ];
  }, [createNewOption, normalizedItems]);

  const visibleItems = useMemo(() => {
    if (!isSearchable) return allItems;
    return filterSelectItems(allItems, searchQuery, [CREATE_NEW_KEY]);
  }, [allItems, isSearchable, searchQuery]);

  const itemsBySection = useMemo(() => {
    return visibleItems.reduce(
      (acc, item) => {
        const section = item.sectionName || "default";
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(item);
        return acc;
      },
      {} as Record<string, LimeSelectV3Item[]>,
    );
  }, [visibleItems]);

  const selectedKeys = useMemo(() => toSelectedKeys(value), [value]);

  const allItemKeys = useMemo(() => {
    return visibleItems.flatMap((item) =>
      item.key !== CREATE_NEW_KEY && !disabledKeySet.has(item.key)
        ? [item.key]
        : [],
    );
  }, [disabledKeySet, visibleItems]);

  const currentSelectedKeys = useMemo(() => {
    if (!isMultiple) return [];
    return Array.isArray(value) ? selectedKeys : [];
  }, [isMultiple, selectedKeys, value]);

  const areAllItemsSelected = useMemo(() => {
    return (
      allItemKeys.length > 0 &&
      allItemKeys.every((key) => currentSelectedKeys.includes(key))
    );
  }, [allItemKeys, currentSelectedKeys]);

  const handleSelectAllToggle = useCallback(() => {
    if (!isMultiple) return;

    if (areAllItemsSelected) {
      const remaining = currentSelectedKeys.filter(
        (key) => !allItemKeys.includes(key),
      );

      if (remaining.length === 0 && disallowEmptySelection) {
        onMultipleItemChange?.(
          normalizedItems[0] != null ? [normalizedItems[0].key] : [],
        );
      } else {
        onMultipleItemChange?.(remaining);
      }
      return;
    }

    const merged = [
      ...new Set([...currentSelectedKeys, ...allItemKeys]),
    ].filter((key) => !key.startsWith("__select_all"));
    onMultipleItemChange?.(merged);
  }, [
    allItemKeys,
    areAllItemsSelected,
    currentSelectedKeys,
    disallowEmptySelection,
    isMultiple,
    normalizedItems,
    onMultipleItemChange,
  ]);

  const handleSectionSelectAllToggle = useCallback(
    (sectionKeys: string[]) => {
      if (!isMultiple) return;

      const allSectionSelected = sectionKeys.every((key) =>
        currentSelectedKeys.includes(key),
      );

      if (allSectionSelected) {
        const remaining = currentSelectedKeys.filter(
          (key) => !sectionKeys.includes(key),
        );

        if (remaining.length === 0 && disallowEmptySelection) {
          onMultipleItemChange?.(
            normalizedItems[0] != null ? [normalizedItems[0].key] : [],
          );
        } else {
          onMultipleItemChange?.(remaining);
        }
        return;
      }

      const merged = [
        ...new Set([...currentSelectedKeys, ...sectionKeys]),
      ].filter((key) => !key.startsWith("__select_all"));
      onMultipleItemChange?.(merged);
    },
    [
      currentSelectedKeys,
      disallowEmptySelection,
      isMultiple,
      normalizedItems,
      onMultipleItemChange,
    ],
  );

  function handleChange(next: Key | Key[] | null) {
    const keys = toSelectedKeys(next);

    if (keys.includes(CREATE_NEW_KEY)) {
      createNewOption?.onCreateNew();
      return;
    }

    if (isMultiple) {
      if (keys.some((key) => key.startsWith("__select_all"))) {
        handleSelectAllToggle();
        return;
      }

      if (keys.length === 0 && disallowEmptySelection) {
        return;
      }

      onMultipleItemChange?.(keys);
      return;
    }

    const key = keys[0] ?? "";
    if (key === "" && disallowEmptySelection) {
      return;
    }

    onSingleItemChange?.(key);
    onChange?.(key);
  }

  const sectionNames = Object.keys(itemsBySection);
  const hiddenSelectedItems = useMemo(() => {
    if (visibleItems.length === 0 || visibleItems.length >= allItems.length) {
      return [];
    }

    return allItems.filter(
      (item) =>
        selectedKeys.includes(item.key) &&
        !visibleItems.some((visible) => visible.key === item.key),
    );
  }, [allItems, selectedKeys, visibleItems]);

  if (isSkeleton) {
    return <Skeleton className={cn("h-10 w-full rounded-xl", className)} />;
  }

  const selectValue = isMultiple ? selectedKeys : (selectedKeys[0] ?? null);

  return (
    <Select
      aria-label={
        ariaLabel ?? (typeof label === "string" ? undefined : "select")
      }
      className={cn("w-full", className)}
      data-identifier={dataIdentifier}
      disabledKeys={disabledKeySet}
      fullWidth
      isDisabled={isDisabled || isLoading}
      isInvalid={isInvalid || !!error}
      isOpen={isOpen}
      isRequired={isRequired}
      name={name}
      onChange={handleChange}
      onOpenChange={(open) => {
        if (!open) setSearchQuery("");
        onOpenChange?.(open);
      }}
      placeholder={placeholder}
      selectionMode={selectionMode}
      value={selectValue}
      variant={variant}
    >
      {label != null ? (
        <Label isInvalid={isInvalid || !!error}>{label}</Label>
      ) : null}
      <Select.Trigger className={triggerClassName}>
        {startContent != null ? (
          <span className="me-2 flex shrink-0 items-center text-muted">
            {startContent}
          </span>
        ) : null}
        <Select.Value>
          {({ defaultChildren, isPlaceholder, state }) => {
            if (isPlaceholder || state.selectedItems.length === 0) {
              return defaultChildren;
            }

            const selected = state.selectedItems.flatMap((item) => {
              const match = allItems.find(
                (option) => option.key === String(item.key),
              );
              return match == null ? [] : [match];
            });

            if (selected.length === 0) {
              return defaultChildren;
            }

            if (renderValue) {
              return renderValue(selected);
            }

            if (selected.length === 1) {
              const [only] = selected;
              return only.customLabel ?? only.label;
            }

            return selected.map((item) => item.label).join(", ");
          }}
        </Select.Value>
        <Select.Indicator />
      </Select.Trigger>
      {description != null ? <Description>{description}</Description> : null}
      <Select.Popover>
        {isSearchable ? (
          <div className="sticky top-0 z-30 bg-overlay px-1 pt-1 pb-2">
            <SearchField
              aria-label={searchPlaceholder ?? t`Išči`}
              className="w-full"
              onChange={setSearchQuery}
              onKeyDown={(event) => event.stopPropagation()}
              value={searchQuery}
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input
                  className="w-full"
                  placeholder={searchPlaceholder ?? t`Išči`}
                />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          </div>
        ) : null}
        {visibleItems.length === 0 ? (
          <p className="px-3 py-2 text-muted text-sm">{emptyContentMessage}</p>
        ) : (
          <ListBox selectionMode={selectionMode}>
            {showSelectAllToggle && isMultiple && allItemKeys.length > 0 ? (
              <ListBox.Item
                id={SELECT_ALL_ITEM_KEY}
                textValue={
                  areAllItemsSelected ? t`Prekliči izbiro vseh` : t`Izberi vse`
                }
              >
                {areAllItemsSelected ? (
                  <List size={16} />
                ) : (
                  <ListChecks size={16} />
                )}
                {areAllItemsSelected ? t`Prekliči izbiro vseh` : t`Izberi vse`}
                {areAllItemsSelected ? <Check size={14} /> : null}
              </ListBox.Item>
            ) : null}
            {sectionNames.map((sectionName, index) => {
              const sectionItems = itemsBySection[sectionName] ?? [];

              if (sectionName === "default") {
                return sectionItems.map((item) => (
                  <OptionItem
                    item={item}
                    key={item.key}
                    selectIdentifier={dataIdentifier}
                  />
                ));
              }

              const sectionKeys = sectionItems.map((item) => item.key);
              const allSectionSelected =
                showSectionSelectAllToggle &&
                isMultiple &&
                sectionKeys.length > 0 &&
                sectionKeys.every((key) =>
                  currentSelectedKeys.includes(key as string),
                );

              return (
                <Fragment key={sectionName}>
                  {index > 0 ? <Separator /> : null}
                  <ListBox.Section>
                    <Header className="sticky top-1 z-20 flex w-full rounded-small bg-default-100 px-2 py-1.5 shadow-small">
                      {showSectionSelectAllToggle && isMultiple ? (
                        <Checkbox
                          className="w-full"
                          isSelected={allSectionSelected}
                          onChange={() =>
                            handleSectionSelectAllToggle(
                              sectionKeys as string[],
                            )
                          }
                        >
                          <Checkbox.Content
                            onPointerDown={(event) => event.stopPropagation()}
                          >
                            <Checkbox.Control>
                              <Checkbox.Indicator />
                            </Checkbox.Control>
                            <span className="text-gray-500 text-xs">
                              {sectionName}
                            </span>
                          </Checkbox.Content>
                        </Checkbox>
                      ) : (
                        sectionName
                      )}
                    </Header>
                    {sectionItems.map((item) => (
                      <OptionItem
                        item={item}
                        key={item.key}
                        selectIdentifier={dataIdentifier}
                      />
                    ))}
                  </ListBox.Section>
                </Fragment>
              );
            })}
            {hiddenSelectedItems.map((item) => (
              <OptionItem
                hidden
                item={item}
                key={`hidden-${item.key}`}
                selectIdentifier={dataIdentifier}
              />
            ))}
          </ListBox>
        )}
      </Select.Popover>
      {error != null && error !== false ? (
        <FieldError>{error}</FieldError>
      ) : null}
    </Select>
  );
}

function OptionItem({
  hidden = false,
  item,
  selectIdentifier,
}: {
  hidden?: boolean;
  item: LimeSelectV3Item;
  selectIdentifier?: string;
}) {
  const hasIndicator = item.key !== CREATE_NEW_KEY;

  return (
    <ListBox.Item
      // The check indicator is absolutely placed at the end, so the label
      // needs the room reserved or a long one runs underneath it.
      className={cn(hidden && "hidden", hasIndicator && "pe-8")}
      data-identifier={
        selectIdentifier ? `${selectIdentifier}-option-${item.key}` : undefined
      }
      id={item.key}
      textValue={item.label}
    >
      {item.startContent}
      <span className="min-w-0 flex-1 truncate">
        {item.customLabel ?? item.label}
      </span>
      {item.endContent}
      {hasIndicator ? <ListBox.ItemIndicator /> : null}
    </ListBox.Item>
  );
}

function toSelectedKeys(value: unknown): string[] {
  if (value == null || value === "") {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(String);
  }
  return [String(value)];
}
